import { AxiosError } from 'axios';
import { Form, FormikHelpers } from 'formik';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { payoutRequisitesApi, requisitesApi } from 'api';
import {
  CloseDialogHandler,
  CloseFormikDialogResult,
  FormControls,
  FormikDialog,
  FormikTextField,
} from 'components';
import { useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PayoutRequisites, Requisites } from 'types';

type Values = {
  blockedMessage: string;
};

type Props = {
  open: boolean;
  isPayout?: boolean;
  requisites?: Requisites | PayoutRequisites;
  onClose: CloseDialogHandler;
};

export const BlockRequisitesDialog: React.FC<Props> = ({
  open,
  isPayout,
  requisites,
  onClose,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites.requisites_list',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        blockedMessage: Yup.string().required(tCommon('errors.required')),
      }),
    [tCommon],
  );

  const initialValues: Values = useMemo(
    () => ({
      blockedMessage: '',
    }),
    [],
  );

  const blockFn: (params: {
    id: string;
    blockedMessage: string;
  }) => Promise<any> = useMemo(
    () => (isPayout ? payoutRequisitesApi.block : requisitesApi.block),
    [isPayout],
  );

  const blockMutation = useMutation<
    Requisites | PayoutRequisites,
    AxiosError,
    { id: string; blockedMessage: string }
  >(blockFn, {
    notifierType: 'execute',
  });

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      if (requisites) {
        blockMutation.mutate(
          {
            id: requisites.id,
            blockedMessage: values.blockedMessage,
          },
          {
            onSuccess: () => {
              formikHelpers.resetForm();
              onClose({ ok: true });
            },
          },
        );
      }
    },
    [requisites, blockMutation, onClose],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        handleSubmit(result.data.values, result.data.formikHelpers);
      } else {
        blockMutation.reset();
        onClose({ ok: result.ok });
      }
    },
    [blockMutation, handleSubmit, onClose],
  );

  return (
    <FormikDialog
      formikProps={{
        initialValues,
        validationSchema,
        validateOnMount: true,
      }}
      dialogProps={{
        open,
        mutation: blockMutation,
        title: t('block_dialog.title'),
        onClose: handleClose,
      }}
    >
      {() => (
        <Form>
          <FormControls>
            <FormikTextField
              className="tw-w-full"
              label={t('block_dialog.message')}
              name="blockedMessage"
            />
          </FormControls>
        </Form>
      )}
    </FormikDialog>
  );
};
